import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { graphql, usePreloadedQuery, type PreloadedQuery } from 'react-relay';
import { RightActionsWrapper } from '@atlassian/jira-business-app-wrapper/src/ui/actions-wrapper/index.tsx';
import { QueryError } from '@atlassian/jira-business-error-handling/src/utils/query-error/index.tsx';
import {
	ExperienceStart,
	useExperienceAbort,
	useExperienceFail,
} from '@atlassian/jira-business-experience-tracking/src/controllers/experience-tracker/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { UNSAFE_noExposureExp } from '@atlassian/jira-feature-experiments';
import { useFlagsService, type FlagConfiguration } from '@atlassian/jira-flags';
import { useRenameProject } from '@atlassian/jira-horizontal-nav-common-old/src/services/rename-project/index.tsx';
import { useFeatures } from '@atlassian/jira-navigation-apps-sidebar-core/src/controllers/index.tsx';
import { NavigationKitAvatar } from '@atlassian/jira-navigation-kit-avatar/src/ui/NavigationKitAvatar.tsx';
import { ErrorBoundary as NavigationErrorBoundary } from '@atlassian/jira-horizontal-nav-error-boundary/src/ui/agg-error-boundary/index.tsx';
import type { RelayAGGError } from '@atlassian/jira-horizontal-nav-error-boundary/src/ui/agg-error-boundary/utils.tsx';
import Header, {
	type WithInlineEditProps,
} from '@atlassian/jira-navigation-kit-header-old/src/index.tsx';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import {
	fireTrackAnalytics,
	useAnalyticsEvents,
	ContextualAnalyticsData,
	fireUIAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import ProjectCreateImportProgress from '@atlassian/jira-project-create-import-progress/src/index.tsx';
import { ThemePicker as ThemePickerNav3 } from '@atlassian/jira-project-theme-components/src/ui/theme-picker-nav3/ThemePicker.tsx';
import { useThemeSetting } from '@atlassian/jira-project-theme-providers/src/controllers/theme/index.tsx';
import { useRelayResource } from '@atlassian/jira-relay-utils/src/services/resources/index.tsx';
import type { projectNavigation_horizontalNavigationQuery as BusinessHorizontalNavQuery } from '@atlassian/jira-relay/src/__generated__/projectNavigation_horizontalNavigationQuery.graphql';
import { businessHorizontalNav3Resource } from '@atlassian/jira-router-resources-business-navigation-items/src/index.tsx';
import { useIsAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-admin/index.tsx';
import JwmAutomationDiscoveryExperimentAutomationDrawerButton from '@atlassian/jira-work-management-moon-experiment-automation-discovery/src/index.tsx';
import { useQueryParam } from '@atlassian/react-resource-router';
import {
	CONTAINER,
	PROJECT,
	VIEW_HORIZONTAL_NAVIGATION_EXPERIENCE,
} from '../../common/constants.tsx';
import NavWrapper from '../../common/ui/nav-wrapper/index.tsx';
import HeaderSkeleton from '../../common/ui/skeleton/header/index.tsx';
import { SkeletonComponent as TabsSkeleton } from '../../common/ui/skeleton/horizontal-nav/index.tsx';
import { CustomTemplateBusinessProjectBanner } from '../../common/ui/custom-template-business-project-banner/index.tsx';
import messages from './messages.tsx';
import ProjectSettingsButton from './project-settings-button/index.tsx';
import Tabs from './tabs/index.tsx';
import { getFlagDetails, HANDLED_STATUS_CODES } from './utils.tsx';

const isJwmAutomationDiscoveryExperimentEnabled = () => {
	const [expConfig] = UNSAFE_noExposureExp('jwm_automation_templates_discovery');

	return expConfig.get('cohort', 'not-enrolled') !== 'not-enrolled';
};

const HEADER_TEST_ID = 'business-horizontal-nav.ui.project-header';
const REDIRECT_FLAG: FlagConfiguration = {
	type: 'info',
	title: messages.defaultViewRedirectFlagTitle,
	description: messages.defaultViewRedirectFlagDescription,
	messageId: 'business-horizontal-nav.ui.project-navigation.redirect-flag.info',
	messageType: 'transactional',
};
const REDIRECT_QUERY_PARAM = 'redirect';

const PACKAGE_NAME = 'jiraBusinessHorizontalNav';

type Props = {
	queryRef: PreloadedQuery<BusinessHorizontalNavQuery>;
	handleError: (error: Error | QueryError | RelayAGGError) => void;
};

/**
 * @deprecated remove when cleaning up getWillShowNav4
 */
export const ProjectNavigationContent = ({ queryRef, handleError }: Props) => {
	const data = usePreloadedQuery<BusinessHorizontalNavQuery>(
		graphql`
			query projectNavigation_horizontalNavigationQuery(
				$cloudId: ID!
				$projectIdOrKey: String!
				$first: Int!
				$projectKey: String!
				$projectAri: ID!
				$useProjectKey: Boolean!
				$isJiraCustomTemplatesUxEnabled: Boolean!
			) {
				jira @required(action: THROW) {
					...tabs_businessHorizontalNav_TabsContent
					projectDataByKey: jiraProjectByKey(cloudId: $cloudId, key: $projectKey)
						@include(if: $useProjectKey) {
						avatar {
							large
						}
						projectId @required(action: THROW)
						projectKey: key
						name
						canEditProjectConfig: action(type: EDIT_PROJECT_CONFIG) {
							canPerform
						}
						...customTemplateBusinessProjectBanner_businessHorizontalNav
							@include(if: $isJiraCustomTemplatesUxEnabled)
					}
					projectDataById: jiraProject(id: $projectAri) @skip(if: $useProjectKey) {
						avatar {
							large
						}
						projectId @required(action: THROW)
						projectKey: key
						name
						canEditProjectConfig: action(type: EDIT_PROJECT_CONFIG) {
							canPerform
						}
						...customTemplateBusinessProjectBanner_businessHorizontalNav
							@include(if: $isJiraCustomTemplatesUxEnabled)
					}
				}
			}
		`,
		queryRef,
	);

	const { data: featuresData } = useFeatures();
	const projectData = data.jira.projectDataByKey
		? data.jira.projectDataByKey
		: data.jira.projectDataById;
	const [isThemePickerOpen, setIsThemePickerOpen] = useState(false);
	// eslint-disable-next-line react-hooks/rules-of-hooks
	const [theme] = fg('jira_theming_relay_migration') ? [undefined] : useThemeSetting();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { showFlag } = useFlagsService();
	const [redirectQueryParam, setRedirectQueryParam] = useQueryParam(REDIRECT_QUERY_PARAM);
	const isAdmin = useIsAdmin();
	const { updateProjectName, validateProjectName } = useRenameProject();

	useEffect(() => {
		if (redirectQueryParam === 'viewMissing') {
			setRedirectQueryParam(undefined, 'replace');
			fireTrackAnalytics(createAnalyticsEvent({}), 'defaultView redirectedTo', {});
			showFlag(REDIRECT_FLAG);
		}
	}, [showFlag, setRedirectQueryParam, redirectQueryParam, createAnalyticsEvent]);

	const editableAvatar = useMemo(
		() =>
			projectData == null ? null : (
				<NavigationKitAvatar
					projectId={Number(projectData?.projectId)}
					selectedAvatarUrl={projectData?.avatar?.large ?? ''}
					canEdit={projectData?.canEditProjectConfig?.canPerform ?? false}
				/>
			),
		[projectData],
	);

	const extraComponentsFar = (
		<RightActionsWrapper>
			{isJwmAutomationDiscoveryExperimentEnabled() ? (
				<JwmAutomationDiscoveryExperimentAutomationDrawerButton />
			) : null}
			{featuresData?.hasSettings ? <ProjectSettingsButton /> : null}
		</RightActionsWrapper>
	);

	useEffect(() => {
		!fg('jira_theming_relay_migration') &&
			fireTrackAnalytics(createAnalyticsEvent({}), 'theme evaluated', {
				theme: theme?.value,
				projectId: projectData?.projectId,
			});
	}, [createAnalyticsEvent, theme?.value, projectData?.projectId]);

	const onUpdateProjectName = useCallback(
		(name: string) => updateProjectName(Number(projectData?.projectId), name),
		[projectData?.projectId, updateProjectName],
	);

	let headerProps = {};
	if (projectData?.projectId) {
		const inlineEditProps: WithInlineEditProps = {
			withInlineEdit: true,
			onConfirm: onUpdateProjectName,
			onValidate: validateProjectName,
			id: Number(projectData?.projectId),
		};

		headerProps = isAdmin ? inlineEditProps : {};
	}

	if (fg('issue_terminology_refresh_change_boarding_modal')) {
		const analyticsEvent = createAnalyticsEvent({
			actionSubject: 'projectOrIssuePage',
			action: 'viewed',
		});
		fireUIAnalytics(analyticsEvent, 'projectOrIssueScreen');
	}

	return (
		<>
			{projectData && fg('jira_custom_templates_ux') && (
				<CustomTemplateBusinessProjectBanner projectFragment={projectData} />
			)}
			<NavWrapper>
				<Header
					title={projectData?.name}
					{...headerProps}
					extraComponentsClose={
						<>
							{featuresData?.hasChangeTheme && !!projectData?.projectId && (
								<ThemePickerNav3
									isOpen={isThemePickerOpen}
									onOpenChange={setIsThemePickerOpen}
									projectId={Number(projectData.projectId)}
								/>
							)}

							{projectData?.projectKey && (
								<ProjectCreateImportProgress
									projectKey={projectData?.projectKey}
									projectName={projectData?.name}
								/>
							)}
						</>
					}
					icon={editableAvatar}
					isLoading={projectData == null}
					testId={HEADER_TEST_ID}
					extraComponentsFar={extraComponentsFar}
				/>
				<Placeholder name="BusinessHorizontalNavigationTabs" fallback={<TabsSkeleton />}>
					<Tabs jiraQueryFragment={data.jira} handleError={handleError} />
				</Placeholder>
			</NavWrapper>
		</>
	);
};

const ProjectNavigation = () => {
	const { showFlag } = useFlagsService();
	const { queryReference } = useRelayResource<BusinessHorizontalNavQuery>(
		businessHorizontalNav3Resource,
	);
	const queryReferenceRef = useRef<PreloadedQuery<BusinessHorizontalNavQuery> | null>(null);
	const abortExperience = useExperienceAbort(VIEW_HORIZONTAL_NAVIGATION_EXPERIENCE);
	const failExperience = useExperienceFail(VIEW_HORIZONTAL_NAVIGATION_EXPERIENCE);

	if (queryReference) {
		queryReferenceRef.current = queryReference;
	}

	const handleError = useCallback(
		(error: Error | QueryError | RelayAGGError) => {
			const statusCode = error && 'statusCode' in error ? Number(error.statusCode) : 0;
			const flagDetails = getFlagDetails(statusCode);

			showFlag(flagDetails);

			if (HANDLED_STATUS_CODES.includes(statusCode)) {
				abortExperience('handled client side error');
				return;
			}

			failExperience('loading horizontal navigation failed', error);
			// only fire error analytics for non-handled query errors
			if (error instanceof QueryError) {
				fireErrorAnalytics({
					meta: {
						id: 'businessHorizontalNavigationQuery',
						packageName: PACKAGE_NAME,
						teamName: 'wanjel',
					},
					error,
					sendToPrivacyUnsafeSplunk: true,
				});
			}
		},
		[abortExperience, failExperience, showFlag],
	);

	const analyticsData = useMemo(
		() => ({
			navigationLayer: CONTAINER,
			containerType: PROJECT,
			withContainerNav: false,
		}),
		[],
	);

	return (
		<ContextualAnalyticsData attributes={analyticsData}>
			<ExperienceStart experience={VIEW_HORIZONTAL_NAVIGATION_EXPERIENCE} />
			<JSErrorBoundary
				id="BusinessHorizontalNavigationErrorBoundary"
				packageName={PACKAGE_NAME}
				onError={(_, error) => handleError(error)}
				fallback={HeaderSkeleton}
			>
				<NavigationErrorBoundary fallback={<HeaderSkeleton />} handleError={handleError}>
					<Placeholder name="BusinessHorizontalNavigation" fallback={<HeaderSkeleton />}>
						{queryReferenceRef.current && (
							<ProjectNavigationContent
								queryRef={queryReferenceRef.current}
								handleError={handleError}
							/>
						)}
					</Placeholder>
				</NavigationErrorBoundary>
			</JSErrorBoundary>
		</ContextualAnalyticsData>
	);
};

export default memo(ProjectNavigation);
